import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Input, Select, Button, Spin } from "antd";
import ExportarReporteAprobados from "./ExportarRerporteAprobados";
import ExportarResumenAprobados from "./ExportarResumenAprobados";
import debounce from "lodash/debounce";
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';
import { obtenerAprobados } from "../../services/reporteKokam/aprobadosKokanReporte";

const { Option } = Select;

const ReporteAprobados = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [filtroMunicipio, setFiltroMunicipio] = useState("");
    const [filtroEncuestador, setFiltroEncuestador] = useState("");
    const [filtroCedula, setFiltroCedula] = useState("");
    const [filtroCedulaEncuestador, setFiltroCedulaEncuestador] = useState(""); // 🆕
    const [filtroAprobacionPago, setFiltroAprobacionPago] = useState("");
    const [modoResumen, setModoResumen] = useState(false);

    //Control de Permisos
    const appName = 'aprobados-kokan';
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    const permissionAccess = permissions.priv_access === 'Y';
    const permissionPrint = permissions.priv_print === 'Y';
    const permissionDelete = permissions.priv_delete === 'Y';

    const user = useSelector((state) => state.auth.user);
    const userLogin = user?.username;
    const isAdmin = user?.priv_admin === true || user?.role === "1";
    const isRol2 = user?.role === "2";
    const isRol3 = user?.role === "3";
    const municipioUser = user?.cod_municipio;
    const nameUser = user?.name;

    const [loading, setLoading] = useState(false);
    const [pageSizeDetalle, setPageSizeDetalle] = useState(100);
    const [pageSizeResumen, setPageSizeResumen] = useState(100);

    const construirParams = useCallback(() => {
        if (isAdmin) return { role: "admin" };
        if (isRol2) return { role: "2", municipio: municipioUser };
        if (isRol3) return { role: "3", username: userLogin };
        return {};
    }, [isAdmin, isRol2, isRol3, municipioUser, userLogin]);

    // ── Carga inicial ─────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const params = construirParams();
                const response = await obtenerAprobados(params);

                if (response.ok) {
                    setData(response.data);
                    setFilteredData(response.data);
                } else {
                    console.error(response.message);
                }
            } catch (error) {
                console.error("Error en fetchData:", error);
            } finally {
                setLoading(false);
            }
        };

        if (user) fetchData();
    }, [user, construirParams]);

    // 🔍 FILTROS (reactivos)
    useEffect(() => {
        let result = [...data];

        if (filtroMunicipio) {
            result = result.filter((item) =>
                item.municipio_participante_nombre
                    ?.toLowerCase()
                    .includes(filtroMunicipio.toLowerCase())
            );
        }

        if (filtroEncuestador) {
            result = result.filter((item) =>
                `${item.nombres_promotor} ${item.apellidos_promotor}`
                    .toLowerCase()
                    .includes(filtroEncuestador.toLowerCase())
            );
        }

        if (filtroCedula) {
            result = result.filter((item) =>
                item.identificacion?.includes(filtroCedula)
            );
        }

        // 🆕 Filtro por cédula del encuestador
        if (filtroCedulaEncuestador) {
            result = result.filter((item) =>
                item.cedula_promotor?.includes(filtroCedulaEncuestador)
            );
        }

        if (filtroAprobacionPago) {
            result = result.filter((item) =>
                item.aprobacionPago?.toString().toUpperCase().trim() === filtroAprobacionPago
            );
        }

        setFilteredData(result);
    }, [filtroMunicipio, filtroEncuestador, filtroCedula, filtroCedulaEncuestador, filtroAprobacionPago, data]);

    // 🔥 DEBOUNCE
    const handleFiltroCedula = useMemo(
        () => debounce((value) => setFiltroCedula(value), 300),
        []
    );

    // 🆕 DEBOUNCE para cédula encuestador
    const handleFiltroCedulaEncuestador = useMemo(
        () => debounce((value) => setFiltroCedulaEncuestador(value), 300),
        []
    );

    // 🧹 CLEANUP
    useEffect(() => {
        return () => {
            handleFiltroCedula.cancel();
            handleFiltroCedulaEncuestador.cancel(); // 🆕
        };
    }, [handleFiltroCedula, handleFiltroCedulaEncuestador]);

    const limpiarFiltros = () => {
        setFiltroMunicipio("");
        setFiltroEncuestador("");
        setFiltroCedula("");
        setFiltroCedulaEncuestador(""); // 🆕
        setFiltroAprobacionPago("");
        setFilteredData(data);
    };

    // 📊 COLUMNAS DETALLE
    const columns = [
        {
            title: "Cédula titular",
            dataIndex: "identificacion",
        },
        {
            title: "Nombre titular",
            dataIndex: "nombre_completo",
        },
        {
            title: "Municipio titular",
            dataIndex: "municipio_participante_nombre",
        },
        {
            title: "Validación KOKAN",
            dataIndex: "validacion_municipio",
            align: "center",
            render: (text) => (
                <span style={{ color: text === "OK" ? "green" : "red" }}>
                    {text}
                </span>
            ),
        },
        {
            title: "Validación pago",
            dataIndex: "aprobacionPago",
            align: "center",
            render: (value) => {
                const val = value?.toString().toUpperCase().trim();

                if (val === "SI") {
                    return <span style={{ color: "green", fontWeight: "bold" }}>Sí</span>;
                }
                if (val === "SI - CORREGIDO") {
                    return <span style={{ color: "#2E7D32", fontWeight: "bold" }}>Sí (Corregido)</span>;
                }
                if (val === "NO") {
                    return <span style={{ color: "red", fontWeight: "bold" }}>No</span>;
                }

                return <span style={{ color: "#999" }}>Sin dato</span>;
            },
        },
        {
            title: "Encuestador",
            render: (_, record) =>
                `${record.nombres_promotor} ${record.apellidos_promotor}`,
        },
        {
            title: "Cédula encuestador",
            dataIndex: "cedula_promotor",
        },
        {
            title: "Teléfono",
            dataIndex: "telefono",
        },
        {
            title: "Dirección",
            dataIndex: "direccion",
        },
        {
            title: "Observaciones",
            dataIndex: "observaciones",
            render: (value) => value ?? " - ",
        },
    ];

    // 📊 COLUMNAS RESUMEN
    const columnsResumen = [
        { title: "Municipio", dataIndex: "municipio" },
        { title: "Encuestador", dataIndex: "encuestador" },
        { title: "Cédula encuestador", dataIndex: "cedula" },
        { title: "Encuestas aprobadas KOKAN", dataIndex: "total", align: "center" },
        { title: "Encuestas aprobadas para pago", dataIndex: "totalPagos", align: "center" },
    ];

    // 🧠 Opciones dinámicas
    const municipios = [...new Set(data.map((item) => item.municipio_participante_nombre))];
    const encuestadores = [...new Set(data.map((item) => `${item.nombres_promotor} ${item.apellidos_promotor}`))];

    // ✅ RESUMEN con totalPagos corregido
    const resumenData = useMemo(() => {
        const mapa = {};

        filteredData.forEach((item) => {
            const key = item.cedula_promotor;

            if (!mapa[key]) {
                mapa[key] = {
                    key,
                    municipio: item.municipio_participante_nombre,
                    encuestador: `${item.nombres_promotor} ${item.apellidos_promotor}`,
                    cedula: item.cedula_promotor,
                    total: 0,
                    totalPagos: 0, // 🔧 se inicializa
                    detalle: [],
                };
            }

            mapa[key].total += 1;

            // 🔧 Se cuenta si aprobacionPago es "SI" o "SI - CORREGIDO"
            const val = item.aprobacionPago?.toString().toUpperCase().trim();
            if (val === "SI" || val === "SI - CORREGIDO") {
                mapa[key].totalPagos += 1;
            }

            mapa[key].detalle.push(item);
        });

        return Object.values(mapa);
    }, [filteredData]);

    return (
        <div className="container py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0" style={{ color: "#0968c6", fontWeight: "bold" }}>
                    Aprobados KOKAN - {nameUser ? nameUser.toUpperCase() : ""}
                </h4>
                {permissionPrint && (
                    <div className="d-flex gap-2">
                        <Button onClick={() => setModoResumen(!modoResumen)}>
                            {modoResumen ? "Ver detalle" : "Resumen"}
                        </Button>
                    </div>
                )}
            </div>

            <div className="mb-2" style={{ fontWeight: "bold" }}>
                Total registros: {modoResumen ? resumenData.length : filteredData.length}
            </div>

            {/* FILTROS */}
            <div className="row g-3 mb-4">

                {/* 🔎 CÉDULA TITULAR */}
                <div className="col-md-3">
                    <Input
                        placeholder="Buscar por cédula titular"
                        onChange={(e) => handleFiltroCedula(e.target.value)}
                    />
                </div>

                {/* 🆕 CÉDULA ENCUESTADOR */}
                <div className="col-md-3">
                    <Input
                        placeholder="Buscar por cédula encuestador"
                        onChange={(e) => handleFiltroCedulaEncuestador(e.target.value)}
                    />
                </div>

                {/* ✅ VALIDACIÓN PAGO */}
                <div className="col-md-3">
                    <Select
                        placeholder="Filtrar por validación pago"
                        style={{ width: "100%" }}
                        allowClear
                        value={filtroAprobacionPago || undefined}
                        onChange={(value) => setFiltroAprobacionPago(value || "")}
                    >
                        <Option value="SI">Aprobado</Option>
                        <Option value="NO">No aprobado</Option>
                    </Select>
                </div>

                {permissionDelete && (
                    <>
                        {/* 🏙️ MUNICIPIO */}
                        <div className="col-md-3">
                            <Select
                                showSearch
                                placeholder="Filtrar por municipio"
                                style={{ width: "100%" }}
                                allowClear
                                value={filtroMunicipio || undefined}
                                onChange={(value) => setFiltroMunicipio(value || "")}
                                onSearch={(value) => setFiltroMunicipio(value || "")}
                                filterOption={false}
                                notFoundContent="Sin resultados"
                            >
                                {municipios.map((m) => (
                                    <Option key={m} value={m}>{m}</Option>
                                ))}
                            </Select>
                        </div>

                        {/* 👤 ENCUESTADOR */}
                        <div className="col-md-3">
                            <Select
                                showSearch
                                placeholder="Filtrar por encuestador"
                                style={{ width: "100%" }}
                                allowClear
                                value={filtroEncuestador || undefined}
                                onChange={(value) => setFiltroEncuestador(value || "")}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                notFoundContent="Sin resultados"
                            >
                                {encuestadores.map((e) => (
                                    <Option key={e} value={e}>{e}</Option>
                                ))}
                            </Select>
                        </div>
                    </>
                )}

                {/* 🔄 BOTÓN LIMPIAR + EXPORTAR */}
                <div className="col-md-3 d-flex align-items-end gap-2">
                    <Button onClick={limpiarFiltros} style={{ width: "100%" }}>
                        Limpiar filtros
                    </Button>

                    {permissionPrint && (
                        modoResumen ? (
                            <ExportarResumenAprobados data={resumenData} />
                        ) : (
                            <ExportarReporteAprobados data={filteredData.length ? filteredData : data} />
                        )
                    )}
                </div>

            </div>

            {/* TABLA */}
            {permissionAccess ? (
                <Spin spinning={loading} tip="Cargando datos...">
                    <div className="bg-white rounded shadow-sm p-2">
                        {modoResumen ? (
                            <Table
                                columns={columnsResumen}
                                dataSource={resumenData}
                                rowKey="key"
                                pagination={{
                                    pageSize: pageSizeResumen,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["50", "100", "200", "500"],
                                    showTotal: (total) => `Total encuestadores: ${total}`,
                                    onShowSizeChange: (_, size) => setPageSizeResumen(size),
                                    onChange: (_, size) => setPageSizeResumen(size),
                                }}
                                expandable={{
                                    expandedRowRender: (record) => (
                                        <Table
                                            columns={columns}
                                            dataSource={record.detalle}
                                            pagination={false}
                                            rowKey="oid"
                                            size="small"
                                        />
                                    ),
                                }}
                            />
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                rowKey="oid"
                                size="small"
                                scroll={{ x: "max-content" }}
                                bordered
                                pagination={{
                                    pageSize: pageSizeDetalle,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["100", "200", "300", "500", "1000"],
                                    showTotal: (total) => `Total registros: ${total}`,
                                    onShowSizeChange: (_, size) => setPageSizeDetalle(size),
                                    onChange: (_, size) => setPageSizeDetalle(size),
                                }}
                            />
                        )}
                    </div>
                </Spin>
            ) : (
                <div className="bg-white rounded shadow-sm p-3">
                    <h1>Acceso no autorizado</h1>
                </div>
            )}

        </div>
    );
};

export default ReporteAprobados;